'use client'

import { useEffect, useState } from 'react'

/**
 * useScrollBelow
 *
 * Detects if the current vertical scroll position of the window is **below** a
 * given pixel threshold. The threshold is interpreted against the total page
 * height (document height), not the viewport height. This makes it useful when
 * you need to know if the user has scrolled past a specific point on the entire
 * page.
 *
 * @param {number} pixelThreshold - Pixel value from the top of the page to
 *                                  compare `window.scrollY` against.
 * @returns {boolean} `true` if the current scroll position is **greater than or equal to**
 *                    `pixelThreshold`, otherwise `false`.
 */
export default function useScrollBelow(pixelThreshold = 0) {
  const [isBelow, setIsBelow] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY || window.pageYOffset || 0
      setIsBelow(scrollY >= pixelThreshold)
    }

    // Trigger once on mount to set the initial value
    handleScroll()

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [pixelThreshold])

  return isBelow
}
